import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { MainLayout } from '../../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { ScrollArea } from '../../components/ui/scroll-area';
import { 
  ArrowLeft, 
  Play, 
  FileText, 
  Calendar, 
  Clock, 
  Star,
  ChevronLeft,
  ChevronRight,
  Download,
  Trash2,
  BarChart3,
  Presentation,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  Mic,
  MessageSquare
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../../components/ui/alert-dialog';
import { toast } from 'sonner';

const EvolveSessionDetail = () => {
  const { sessionId } = useParams();
  const { api } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState(null);
  const [slides, setSlides] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [deleting, setDeleting] = useState(false);
  const [reanalyzing, setReanalyzing] = useState(false);
  const [analysisReadiness, setAnalysisReadiness] = useState(null);

  // Check if session is ready for analysis
  const checkAnalysisReadiness = useCallback(async () => {
    try {
      const res = await api.get(`/evolve/sessions/${sessionId}/analysis-readiness`);
      setAnalysisReadiness(res.data);
    } catch (error) {
      console.error('Failed to check analysis readiness:', error);
    }
  }, [api, sessionId]);

  const handleReanalyze = async () => {
    try {
      setReanalyzing(true);
      toast.info('Re-analyzing your presentation...');
      const response = await api.post(`/evolve/sessions/${sessionId}/analyze`);
      
      if (response.data?.feedback?.overall_score !== undefined) {
        toast.success(`Analysis complete! Score: ${response.data.feedback.overall_score}%`);
        fetchSessionData(); // Refresh session data
      } else {
        toast.success('Analysis complete!');
        fetchSessionData();
      }
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      const errorMessage = typeof errorDetail === 'object' 
        ? errorDetail.recommendation || errorDetail.message 
        : (error.response?.data?.detail || 'Re-analysis failed');
      
      toast.error('Analysis could not be completed', {
        description: errorMessage,
        duration: 6000
      });
    } finally {
      setReanalyzing(false);
    }
  };

  const fetchSessionData = useCallback(async () => {
    try {
      setLoading(true);
      
      const [sessionRes, slidesRes] = await Promise.all([
        api.get(`/evolve/sessions/${sessionId}`),
        api.get(`/evolve/sessions/${sessionId}/slides`).catch(() => ({ data: { slides: [] } }))
      ]);
      
      setSession(sessionRes.data);
      setSlides(slidesRes.data.slides || []);
      
    } catch (error) {
      toast.error('Failed to load session');
      navigate('/evolve');
    } finally {
      setLoading(false);
    }
  }, [api, sessionId, navigate]);

  useEffect(() => {
    fetchSessionData();
  }, [fetchSessionData]);

  // Check analysis readiness when session is loaded and not yet analyzed
  useEffect(() => {
    if (session && session.status !== 'analyzed') {
      checkAnalysisReadiness();
    }
  }, [session, checkAnalysisReadiness]);

  const handleDelete = async () => {
    try {
      setDeleting(true);
      await api.delete(`/evolve/sessions/${sessionId}`);
      toast.success('Session deleted');
      navigate('/evolve');
    } catch (error) {
      toast.error('Failed to delete session');
    } finally {
      setDeleting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'analyzed': return 'bg-emerald-100 text-emerald-800';
      case 'completed': return 'bg-blue-100 text-blue-800';
      case 'in_progress': return 'bg-amber-100 text-amber-800';
      default: return 'bg-slate-100 text-slate-800';
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-emerald-600';
    if (score >= 60) return 'text-amber-600';
    return 'text-rose-600';
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="min-h-screen bg-[#F9F8F6] p-6 lg:p-8">
          <div className="max-w-6xl mx-auto">
            <Skeleton className="h-8 w-48 mb-6" />
            <div className="grid lg:grid-cols-3 gap-6">
              <Skeleton className="lg:col-span-2 h-[500px]" />
              <Skeleton className="h-[500px]" />
            </div>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="min-h-screen bg-[#F9F8F6]">
        <div className="p-6 lg:p-8 max-w-6xl mx-auto">
          
          {/* Header */}
          <div className="mb-8">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/evolve')}
              className="mb-4 -ml-2 text-slate-600 hover:text-slate-900"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Dashboard
            </Button>
            
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {session?.topic_name}
                  </h1>
                  <Badge className={getStatusColor(session?.status)} variant="secondary">
                    {session?.status === 'analyzed' ? 'Completed' : session?.status?.replace('_', ' ')}
                  </Badge>
                </div>
                <p className="text-slate-600 mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {session?.concept_overview}
                </p>
                <div className="flex items-center gap-4 text-sm text-slate-500">
                  <span className="flex items-center gap-1">
                    <Calendar className="h-4 w-4" />
                    {formatDate(session?.created_at)}
                  </span>
                  <span className="flex items-center gap-1">
                    <FileText className="h-4 w-4" />
                    {session?.slide_count || 0} slides
                  </span>
                  {session?.overall_score && (
                    <span className={`flex items-center gap-1 font-semibold ${getScoreColor(session.overall_score)}`}>
                      <Star className="h-4 w-4" />
                      {session.overall_score}%
                    </span>
                  )}
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                {session?.status === 'analyzed' && (
                  <Button 
                    variant="outline"
                    onClick={() => navigate(`/evolve/feedback/${sessionId}`)}
                    className="border-[#E07A5F] text-[#E07A5F] hover:bg-[#E07A5F]/10"
                    data-testid="view-feedback-btn"
                  >
                    <BarChart3 className="h-4 w-4 mr-2" />
                    View Feedback
                  </Button>
                )}
                
                {/* Re-analyze button for completed but not analyzed sessions */}
                {(session?.status === 'completed' || (session?.status === 'analyzed' && !session?.overall_score)) && (
                  <>
                    {/* Analysis Readiness Indicator */}
                    {analysisReadiness && !analysisReadiness.can_analyze && (
                      <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-sm">
                        <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
                        <div>
                          <p className="font-medium text-amber-800 dark:text-amber-200">Cannot analyze yet</p>
                          <p className="text-amber-700 dark:text-amber-300 text-xs mt-1">
                            Please complete at least one of the following:
                          </p>
                          <ul className="mt-2 space-y-1">
                            {analysisReadiness.requirements?.map(req => (
                              <li key={req.id} className="flex items-center gap-2 text-xs">
                                {req.status === 'complete' ? (
                                  <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" />
                                ) : req.status === 'optional' ? (
                                  <MessageSquare className="h-3.5 w-3.5 text-blue-500" />
                                ) : (
                                  <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                                )}
                                <span className={req.status === 'complete' ? 'text-emerald-700 dark:text-emerald-300' : 'text-amber-700 dark:text-amber-300'}>
                                  {req.label}: {req.description}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}
                    
                    <Button 
                      variant="outline"
                      onClick={handleReanalyze}
                      disabled={reanalyzing || (analysisReadiness && !analysisReadiness.can_analyze)}
                      className={`border-amber-500 text-amber-600 hover:bg-amber-50 ${
                        analysisReadiness && !analysisReadiness.can_analyze ? 'opacity-50 cursor-not-allowed' : ''
                      }`}
                      data-testid="reanalyze-btn"
                    >
                      {reanalyzing ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <BarChart3 className="h-4 w-4 mr-2" />
                      )}
                      {reanalyzing ? 'Analyzing...' : 'Analyze Session'}
                    </Button>
                  </>
                )}
                
                {session?.slides_extracted && (
                  <Button 
                    onClick={() => navigate(`/evolve/live/${sessionId}`)}
                    className="bg-[#E07A5F] hover:bg-[#D06A4F] text-white"
                    data-testid="start-session-btn"
                  >
                    <Play className="h-4 w-4 mr-2" />
                    {session?.status === 'analyzed' ? 'Practice Again' : 'Start Session'}
                  </Button>
                )}
                
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="ghost" size="icon" className="text-slate-400 hover:text-rose-500">
                      <Trash2 className="h-5 w-5" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete Session?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This will permanently delete this session and all associated data. This action cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction 
                        onClick={handleDelete}
                        disabled={deleting}
                        className="bg-rose-600 hover:bg-rose-700"
                      >
                        {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Delete'}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="grid lg:grid-cols-3 gap-6">
            
            {/* Slide Viewer */}
            <Card className="lg:col-span-2 bg-white border-slate-200">
              <CardHeader className="border-b border-slate-100">
                <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  <Presentation className="h-5 w-5 text-[#E07A5F]" />
                  Presentation Slides
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                {slides.length > 0 ? (
                  <>
                    {/* Main Slide */}
                    <div className="relative aspect-video bg-slate-100 rounded-xl overflow-hidden mb-4">
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}${slides[currentSlide].url}`}
                        alt={`Slide ${currentSlide + 1}`}
                        className="w-full h-full object-contain"
                      />
                      
                      {/* Navigation */}
                      <button
                        onClick={() => setCurrentSlide(prev => Math.max(0, prev - 1))}
                        disabled={currentSlide === 0}
                        className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/50 text-white disabled:opacity-30 hover:bg-black/70 transition-colors"
                      >
                        <ChevronLeft className="h-6 w-6" />
                      </button>
                      <button
                        onClick={() => setCurrentSlide(prev => Math.min(slides.length - 1, prev + 1))}
                        disabled={currentSlide === slides.length - 1}
                        className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/50 text-white disabled:opacity-30 hover:bg-black/70 transition-colors"
                      >
                        <ChevronRight className="h-6 w-6" />
                      </button>
                      
                      {/* Slide Counter */}
                      <div className="absolute bottom-4 left-1/2 -translate-x-1/2">
                        <Badge variant="secondary" className="bg-black/60 text-white">
                          {currentSlide + 1} / {slides.length}
                        </Badge>
                      </div>
                    </div>
                    
                    {/* Thumbnail Strip */}
                    <ScrollArea className="w-full">
                      <div className="flex gap-2 pb-2">
                        {slides.map((slide, index) => (
                          <button
                            key={slide.slide_number}
                            onClick={() => setCurrentSlide(index)}
                            className={`
                              flex-shrink-0 w-24 h-14 rounded-lg overflow-hidden border-2 transition-all
                              ${currentSlide === index 
                                ? 'border-[#E07A5F] ring-2 ring-[#E07A5F]/30' 
                                : 'border-slate-200 opacity-70 hover:opacity-100'
                              }
                            `}
                          >
                            <img
                              src={`${process.env.REACT_APP_BACKEND_URL}${slide.url}`}
                              alt={`Slide ${slide.slide_number}`}
                              className="w-full h-full object-cover"
                            />
                          </button>
                        ))}
                      </div>
                    </ScrollArea>
                  </>
                ) : (
                  <div className="text-center py-16">
                    <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                      <FileText className="h-8 w-8 text-slate-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      No Slides Available
                    </h3>
                    <p className="text-slate-500" style={{ fontFamily: 'Manrope, sans-serif' }}>
                      {session?.processing_status === 'failed' 
                        ? 'File processing failed. Please try uploading again.' 
                        : session?.processing_status === 'processing'
                          ? 'Slides are being processed...'
                          : 'Upload a presentation to get started.'
                      }
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Session Info */}
            <div className="space-y-6">
              {/* Details Card */}
              <Card className="bg-white border-slate-200">
                <CardHeader>
                  <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Session Details</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <p className="text-sm text-slate-500 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>Topic</p>
                    <p className="font-medium text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{session?.topic_name}</p>
                  </div>
                  
                  <div>
                    <p className="text-sm text-slate-500 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>Overview</p>
                    <p className="text-slate-700 text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>{session?.concept_overview}</p>
                  </div>
                  
                  {session?.notes && (
                    <div>
                      <p className="text-sm text-slate-500 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>Notes</p>
                      <p className="text-slate-700 text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>{session.notes}</p>
                    </div>
                  )}
                  
                  <div className="pt-2 border-t border-slate-100">
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-slate-500" style={{ fontFamily: 'Manrope, sans-serif' }}>Slides</p>
                        <p className="font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>{session?.slide_count || 0}</p>
                      </div>
                      <div>
                        <p className="text-slate-500" style={{ fontFamily: 'Manrope, sans-serif' }}>Storage</p>
                        <p className="font-semibold text-slate-900 capitalize" style={{ fontFamily: 'Outfit, sans-serif' }}>{session?.storage_type || 'local'}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Score Card (if analyzed) */}
              {session?.overall_score && (
                <Card className="bg-gradient-to-br from-[#E07A5F]/10 to-[#81B29A]/10 border-[#E07A5F]/20">
                  <CardContent className="p-6 text-center">
                    <p className="text-sm text-slate-600 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Overall Score</p>
                    <p className={`text-5xl font-bold mb-2 ${getScoreColor(session.overall_score)}`} style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {session.overall_score}%
                    </p>
                    <Button 
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/evolve/feedback/${sessionId}`)}
                      className="mt-2 border-[#E07A5F] text-[#E07A5F] hover:bg-[#E07A5F]/10"
                    >
                      View Full Feedback
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Processing Status (if not complete) */}
              {session?.processing_status && session.processing_status !== 'complete' && (
                <Card className="bg-amber-50 border-amber-200">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <Loader2 className="h-5 w-5 animate-spin text-amber-600" />
                      <div>
                        <p className="font-medium text-amber-800" style={{ fontFamily: 'Outfit, sans-serif' }}>
                          Processing...
                        </p>
                        <p className="text-sm text-amber-600" style={{ fontFamily: 'Manrope, sans-serif' }}>
                          {session.processing_status === 'converting' && 'Converting presentation...'}
                          {session.processing_status === 'extracting' && 'Extracting slides...'}
                          {session.processing_status === 'processing' && 'Processing file...'}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default EvolveSessionDetail;
